import { Injectable } from '@angular/core';
import { Observable, Subscriber } from 'rxjs';

import { HttpClient, HttpRequest, HttpEvent, HttpEventType, HttpHeaderResponse, HttpHeaders } from '@angular/common/http';
import { DateTime } from 'luxon';
import { WebTour } from '../models/webtour.model';
import { PersistenceService } from './persistence.service';
import { TimetableData } from '../models/timetable.model';
import { map } from 'rxjs/operators';
import { PgHelpTicket } from '../models/pg-help.model';
import { PGUtilities } from '../utilities/pg-utilities';
import { ConnectorType, EnvironmentService, RealmData } from './environment.service';
import { PgPostFileData } from '../models/file.model';
import { AuthService } from './auth.service';

export class DataProgress {
    progress: number = 0;
    complete: boolean = false; 
    data:any = null;
    error:HttpHeaderResponse = null;

    constructor(progress:number, complete?:boolean, data?:any, error?:HttpHeaderResponse) {
        this.progress = progress;
        if(complete != null) this.complete = complete;
        if(data != null) this.data = data;
        if(error != null) this.error = error;
    }
}

export class DataFilter {
    constructor(public field: string, 
        public operator: '==' | '!=' | '<' | '>' | '<=' | '>=' | 'contains' | 'startsWith' | 'in' | 'between', 
        public value:Array<any>,
        public resource?:string) {}
}

export class DataOrder {
    constructor(public field?: string, 
        public direction?: 'asc' | 'desc',
        public resource?:string) {}
}

export class PgFilterStatusDataFilter extends DataFilter {
    readonly? :boolean;
}

export class PgFilterStatusDataOrder extends DataOrder {
    readonly? :boolean;
}

export class PgFilterStatus {
    search: string = null;
    tags?: string = null;
    system_tags?: string = null;
    filter: Array<PgFilterStatusDataFilter> = [];
    order: Array<PgFilterStatusDataOrder> = [];
}

export class ModelFieldData {
    name:string = null;
    type:'boolean' | 'date' | 'time' | 'datetime' | 'decimal' | 'float' | 'integer' | 'string' | 'text' | 'blob' = null;
    readonly:boolean = null;
    locale:boolean = null;
    map:{
        multiple: boolean, 
        values: {
            [value:string]: string 
        }
    } = null;
}

export class ModelsData {
    [resource:string]: Array<ModelFieldData>
}

export class RelationData {
    public type:'1:1' | 'N:1' | '1:N' | 'N:N';
    public modelA:string = null;
    public modelB:string = null;
    public joinField:string = null;
}

export class ConfigData {
    public id:string = null;
    public modello:string = null;
    public tipo:string = null;
    public data:string = null;
}

export class PermissionData {
    public id:string = null;
    public model:string = null;
    public role_id:string = null;
    public fields?:string  = null;
    public filter?:string = null;
    public list?:boolean = null;
    public view?:boolean = null;
    public edit?:boolean = null;
    public create?:boolean = null;
    public delete?:boolean = null;
    public ignore_user_group?:boolean = null;
}

export class RoleData {
    public id:string = null;
    public name:string = null;
}

export class GroupData {
    public id:string = null;
    public label:string = null;
}

export class AvailabilityData {
    public start:string = null;
    public end:string = null;
    public status:string = null;
}

export class GetResourceDataOptions {
    public with?:Array<string>;
    public view?:string;
    public search?:string;
    public tags?:string;
    public system_tags?:string;
    public filter?:Array<DataFilter>;
    public order?:Array<DataOrder>;
    public offset?:number;
    public limit?:number;
    public ignore_default_filters?:boolean;
    public realm?:string;
}

export class GetElementSnapshotsOptions {
    public offset?:number;
    public limit?:number;
}

export type AuthLoginMode = 'default'|'otp'|'code'|'token';

export class UserRealmData {
    user_id:string
    realm_id:string
    status:string
    /** ISO datetime of last acceptance, or null if never accepted */
    privacy_policy:string|null
    /** ISO datetime of last acceptance, or null if never accepted */
    terms_and_conditions:string|null
    id:string
}

export class UserDataRealm extends RealmData {
    pivot:UserRealmData = null;

    constructor(fromData?:any) {
        super(fromData)

        if(fromData != null) {
            if(fromData.pivot != null) {
                this.pivot = fromData.pivot
            }
        }
    }
}

export class UserDataRole {
    id:string
    name:string
    pivot: {
        user_id:string,
        role_id:string,
        realm_id:string,
        id:string
    }
}

export class UserDataGroup {
    id:string
    label:string
    pivot: {
        user_id:string,
        group_id:string,
        id:string
    }
}

export class UserLabel {
    id:string
	name:string
	surname:string
}

export class UserData extends UserLabel {
    password?:string
	email:string
	telephone:string
    codice_fiscale:string
	superadmin:1|0
	language:string
    stripe_id:string
	/** ISO datetime of last acceptance, or null if never accepted */
	privacy_policy:string|null
	/** ISO datetime of last acceptance, or null if never accepted */
	terms_and_conditions:string|null
	current_realm: string
    current_role: string
    related_realm:Array<UserDataRealm>
    related_role:Array<UserDataRole>
	related_group:Array<UserDataGroup>
}

export class UserProfileNotificationEventData {
    enabled?:boolean
    channels?:UserProfileNotificationChannelsData

    digest_minutes?:number
}

export type UserProfileNotificationTypesData = 'eatery'|'host'|'experiencesupplier'|'experience'|'article'|'poi'|'itinerary'

export class UserProfileNotificationChannelsData {
    in_app?: boolean
    email?: boolean
    push?: boolean
    sms?: boolean
}

export type UserProfileNotificationChannel = keyof UserProfileNotificationChannelsData

export class UserProfileNotificationData {
    channels:UserProfileNotificationChannelsData = {}
    
    type: {
        host?: boolean,
        experience?: boolean,
        experiencesupplier?: boolean,
        itinerary?: boolean,
        poi?: boolean,
        article?: boolean,
        eatery?: boolean,
    } = {}

    digest_minutes:number = 360
    digest_unify_product_types:boolean = false

    other_realms:boolean = true

    events:{ [event:string]: UserProfileNotificationEventData } = {}

    static readonly EVENT_KEYS = ['accepted_in_realm','rejected_in_realm','user_waiting_in_realm','admin_warning_booking_cancellation','admin_warning_booking_lastconfirm','booked_user_booking_cancel','booked_user_booking_confirmed','broker_booking_confirm','broker_booking_lastconfirm','broker_booking_notification','customer_booking_cancellation','customer_booking_cancellationconfirm','customer_booking_confirm','customer_booking_confirm_online','customer_booking_notification','customer_booking_notification_price_on_request','customer_booking_payment','customer_cumulative_booking_notification','customer_cumulative_booking_notification_price_on_request','supplier_booking_cancellation','supplier_booking_confirm','supplier_booking_notification','supplier_cumulative_booking_not_selected','supplier_cumulative_booking_selected','supplier_host_availability_request','user_booking_cancel','user_booking_confirmed','user_booking_store']

    static readonly EVENT_KEYS_WITH_DIGEST = ['alert_old_product','changes_published_product','import_product','published_product','unpublished_product','upload']

    static readonly TYPE_KEYS:Array<UserProfileNotificationTypesData> = ['eatery','host','experiencesupplier','experience','article','poi','itinerary']

    static readonly CHANNEL_KEYS:Array<UserProfileNotificationChannel> = ['in_app','email','sms','push']

    constructor(fromData?:any) {
        if(fromData) {
            if(fromData.channels != null) this.channels = { ...fromData.channels }
            if(fromData.type != null) this.type = { ...fromData.type }
            if(fromData.digest_minutes != null) this.digest_minutes = fromData.digest_minutes
            if(fromData.digest_unify_product_types != null) this.digest_unify_product_types = fromData.digest_unify_product_types === true
            if(fromData.other_realms != null) this.other_realms = fromData.other_realms === true

            if(fromData.events != null) {
                this.events = {}
                for(let _key in fromData.events) {
                    if(fromData.events[_key] != null) {
                        this.events[_key] = { ...fromData.events[_key] }
                    }
                }
            }
        }
    }
}

export class UserProfileData {
    id:string
    user_id:string
    languages:string
    customer_care:boolean
    company:string
    vat_number:string
    notification_settings:UserProfileNotificationData
}

export class UserAvailabilityData {
    channels: string
    slots: Array<TimetableData>
}

export class SnapshotData {
    id: string
    user_id: string
    resource_id: string
    resource_type: string

    created_at: string
    updated_at: string

    user: { 
        id: string, 
        name: string, 
        surname: string 
    }

    product: any
}

export type ResourceMode = 'data'|'public'|'admin'|'business';

export class ConnectorExportData { 
    product_id: number 
    items: Array<{
        id:number,
        product_id:string,
        new_product_id:string,
        language:string,
        connector:string,
        product_type:string,
        status:string,
        realm:string,
        name_app:string,
        created_at:string,
        updated_at:string,
        product_name:string
    }>
}

@Injectable({
    providedIn: 'root'
})
export class DataService {
    //isCacheEnabled = false;

    selectLimit = 10;

    public status = {
        inProgress: 0
    }

    constructor(private http:HttpClient, private persistenceService:PersistenceService) { 
    }
    
    private environmentService:EnvironmentService = null;
    private authService:AuthService = null;

    init(environmentService:EnvironmentService, authService:AuthService) {
        this.environmentService = environmentService;
        this.authService = authService;
    }

    getRealms() {
        return this.getResourceData('Realm', { limit: 5000 }, null, null, 'public', true) as Observable<Array<RealmData>>
    }

    requestOTP(contact:string, type:'email'|'phone') {
        return new Observable<any>((observer) => {
            this.http.post(this.environmentService.environment.APIUrl + '/otp/' + type + '/' + PGUtilities.cleanRecipient(contact, type), null).subscribe(() => {
                observer.next();
                observer.unsubscribe();
            }, (error) => {
                observer.error(error);
                observer.unsubscribe();
            })
        })
    }

    doLoginOTP(contact:string, type:'email'|'phone', password:string) {
        return new Observable<any>((observer) => {
            this.http.post(this.environmentService.environment.APIUrl + '/otp/login', { contact: PGUtilities.cleanRecipient(contact, type), otp: password }).subscribe((data: any) => {
                observer.next(data);
                observer.unsubscribe();
            }, (error) => {
                observer.error(error);
                observer.unsubscribe();
            })
        })
    }

    doLoginCode(code:string) {
        return new Observable<any>((observer) => {
            this.http.post(this.environmentService.environment.APIUrl + this.environmentService.environment.LoginCodePath.replace('{{code}}', code), null).subscribe((data: any) => {
                observer.next(data);
                observer.unsubscribe();
            }, (error) => {
                observer.error(error);
                observer.unsubscribe();
            })
        })
    }

    doLoginToken(token:string) {
        return new Observable<any>((observer) => {
            this.http.post(this.environmentService.environment.APIUrl + '/login/token', { token: token }).subscribe((data: any) => {
                observer.next(data);
                observer.unsubscribe();
            }, (error) => {
                observer.error(error);
                observer.unsubscribe();
            })
        })
    }

    doLogin(contact:string, password:string) {
        return new Observable<any>((observer) => {
            this.http.post(this.environmentService.environment.APIUrl + '/login', { email: contact, password: password }).subscribe((data: any) => {
                observer.next(data);
                observer.unsubscribe();
            }, (error) => {
                observer.error(error);
                observer.unsubscribe();
            })
        })
    }

    doLogout() {
        return new Observable<any>((observer) => {
            this.http.post(this.environmentService.environment.APIUrl + '/logout', {}).subscribe((data: any) => {
                observer.next();
                observer.unsubscribe();
            }, (error) => {
                observer.next(); // ignoring errors
                observer.unsubscribe();
            })
        })
    }

    refreshToken(mode:AuthLoginMode, token:string) {
        return new Observable<any>((observer) => {
            this.http.post(this.environmentService.environment.APIUrl + (mode == 'default' ? '' : '/' + mode) + '/refresh?refresh_token=' + encodeURIComponent(token), null).subscribe((data:any) => {
                observer.next(data);
                observer.unsubscribe();
            }, (error) => {
                observer.error(error);
                observer.unsubscribe();
            })
        })
    }

    userAccept(data:any, realm:string) {
        return new Observable<any>((observer) => {
            data.realm_id = realm;
            this.http.post(this.environmentService.environment.APIUrl + '/me/acceptRealm', data).subscribe((data: any) => {
                observer.next(data);
                observer.unsubscribe();
            }, (error) => {
                observer.error(error);
                observer.unsubscribe();
            })
        })
    }

    doSessionRefresh() {
        return new Observable<any>((observer) => {
            this.http.post(this.environmentService.environment.APIUrl + '/refresh', {}).subscribe((data: any) => {
                observer.next(data);
                observer.unsubscribe();
            }, (error) => {
                observer.error();
                observer.unsubscribe();
            })
        })
    }

    getUserData(user?:string) {
        return new Observable<UserData>((observer) => {
            let _url = this.environmentService.environment.APIUrl;

            if(user != null) _url += '/' + this._getPathFromResourceMode('admin') + '/user/' + user
            else _url += '/me'

            this.http.get(_url).subscribe((data:any) => {
                observer.next(data);
                observer.unsubscribe();
            }, (error) => {
                observer.next(null);
                observer.unsubscribe();
            })
        })
    }

    setUserData(data:Partial<UserData>, user?:string) {
        return new Observable<UserData>((observer) => {
            let _url = this.environmentService.environment.APIUrl;

            if(user != null) _url += '/' + this._getPathFromResourceMode('admin') + '/user/' + user
            else _url += '/me'

            this.http.put(_url, data, {
                headers:  { 'X-Interceptor-Silent': '422' }
            }).subscribe((data:any) => {
                observer.next(data);
                observer.unsubscribe();
            }, (error) => {
                let _code = null;
                let _text = error.error?.error
                
                if(error.status == 422 && _text) {
                    if(/taken/.test(_text)) {
                        if(/email/.test(_text)) {
                            _code = 'email-taken'
                        }
                        else if(/phone/.test(_text)) {
                            _code = 'phone-taken'
                        }
                    }
                }

                observer.error(_code);
                observer.unsubscribe();
            })
        })
    }

    getUserProfile(user?:string) {
        return new Observable<UserProfileData>((observer) => {
            let _url = this.environmentService.environment.APIUrl;

            if(user != null) _url += '/' + this._getPathFromResourceMode('admin') + '/user/' + user + '/profile'
            else _url += '/me/profile'

            this.http.get(_url).subscribe((data:any) => {
                observer.next(data);
                observer.unsubscribe();
            }, (error) => {
                observer.next(null);
                observer.unsubscribe();
            })
        })
    }

    setUserProfile(data:Partial<UserProfileData>, user?:string) {
        return new Observable<UserProfileData>((observer) => {
            let _url = this.environmentService.environment.APIUrl;

            if(user != null) _url += '/' + this._getPathFromResourceMode('admin') + '/user/' + user + '/profile'
            else _url += '/me/profile'

            this.http.put(_url, data).subscribe((data:any) => {
                observer.next(data);
                observer.unsubscribe();
            }, (error) => {
                observer.next(null);
                observer.unsubscribe();
            })
        })
    }

    getUserAvailability(user?:string) {
        return new Observable<UserAvailabilityData>((observer) => {
            let _url = this.environmentService.environment.APIUrl;

            if(user != null) _url += '/' + this._getPathFromResourceMode('admin') + '/user/' + user + '/availability'
            else _url += '/me/availability'

            this.http.get(_url, {
                headers:  { 'X-Interceptor-Silent': '404' }
            }).subscribe((data:any) => {
                observer.next(data);
                observer.unsubscribe();
            }, (error) => {
                observer.next(null);
                observer.unsubscribe();
            })
        })
    }

    setUserAvailabilitySlots(slots: Array<TimetableData>, user?:string) {
        return new Observable<UserAvailabilityData>((observer) => {
            let _url = this.environmentService.environment.APIUrl;

            if(user != null) _url += '/' + this._getPathFromResourceMode('admin') + '/user/' + user + '/availability/slots'
            else _url += '/me/availability/slots'

            this.http.put(_url, { slots: slots }).subscribe((data:any) => {
                this.getUserAvailability(user).subscribe((data) => {
                    observer.next(data);
                    observer.unsubscribe();
                }, (error) => {
                    observer.next(null);
                    observer.unsubscribe();
                })
            }, (error) => {
                observer.next(null);
                observer.unsubscribe();
            })
        })
    }

    setUserAvailabilityChannels(channels:string, user?:string) {
        return new Observable<UserAvailabilityData>((observer) => {
            let _url = this.environmentService.environment.APIUrl;

            if(user != null) _url += '/' + this._getPathFromResourceMode('admin') + '/user/' + user + '/availability/channels'
            else _url += '/me/availability/channels'

            this.http.put(_url, { channels: JSON.parse(channels) }).subscribe((data:any) => {
                this.getUserAvailability(user).subscribe((data) => {
                    observer.next(data);
                    observer.unsubscribe();
                }, (error) => {
                    observer.next(null);
                    observer.unsubscribe();
                })
            }, (error) => {
                observer.next(null);
                observer.unsubscribe();
            })
        })
    }

    sendHelpTicket(ticket:PgHelpTicket) {
        return new Observable<any>((observer) => {
            let _errorLogData = {
                list: []
            }

            for(let _error of ticket.httpErrorLog) {
                let _responseErrorData = null;

                if(_error.response.error != null) {
                    if(typeof _error.response.error == 'string') {
                        _responseErrorData = _error.response.error;
                    }
                    else {
                        _responseErrorData = {
                            exception: _error.response.error.exception,
                            file: _error.response.error.file,
                            line: _error.response.error.line,
                            message: _error.response.error.message
                        };
                    }
                }

                _errorLogData.list.push({
                    time: _error.time,
                    type: 'http',
                    request: {
                        method: _error.request.method,
                        url: _error.request.url,
                        body: _error.request.body
                    },
                    response: {
                        status: _error.response.status,
                        error: _responseErrorData
                    }
                })
            }

            /*for(let _error of ticket.jsErrorLog) {
                _errorLogData.list.push({
                    time: _error.time,
                    type: 'js',
                    error: _error.error
                })
            }*/

            _errorLogData.list.sort((a, b) => {
                if(a.time < b.time) return -1;
                else if(a.time > b.time) return 1;
                else return 0;
            })

            let _ticketData:any = {
                info: ticket.info,
                error_log: _errorLogData,
            }

            for(let i in ticket) {
                if(typeof ticket[i] != 'object' && typeof ticket[i] != 'function') {
                    if(typeof _ticketData[i] == 'undefined') {
                        _ticketData[i] = ticket[i]
                    }
                }
            }

            this.http.post(this.environmentService.environment.APIUrl + '/help/ticket', _ticketData).subscribe((data: any) => {
                observer.next(data);
                observer.unsubscribe();
            }, (error) => {
                observer.error(error);
                observer.unsubscribe();
            })
        })
    }

    getRequest(queryURL:string, options?:any) {
        return this.http.get(this.environmentService.environment.APIUrl + queryURL, options || {}) as Observable<any>
    }

    postRequest(queryURL:string, sendData:any, options?:any) {
        return this.http.post(this.environmentService.environment.APIUrl + queryURL, sendData, options || {}) as Observable<any>
    }

    putRequest(queryURL:string, sendData:any, options?:any) {
        return this.http.put(this.environmentService.environment.APIUrl + queryURL, sendData, options || {}) as Observable<any>
    }

    deleteRequest(queryURL:string, options?:any) {
        return this.http.delete(this.environmentService.environment.APIUrl + queryURL, options || {}) as Observable<any>
    }

    normalizeResourceName(resourceName:string) {
        return resourceName.charAt(0).toLowerCase() + resourceName.substring(1);
    }

    getResourceFieldName(resourceName:string) {
        return resourceName.replace(/[A-Z]/g, (match) => { return '_' + match.toLowerCase()}).replace(/^_/, '');    
    }

    getModelsData():Observable<ModelsData> {
        return this.getRequest('/model') as Observable<ModelsData>;
    }

    getRelationData():Observable<Array<RelationData>> {
        return this.getRequest('/relation') as Observable<Array<RelationData>>;
    }

    getRolesData(mode?:ResourceMode):Observable<Array<RoleData>> {
        return this.getResourceData('Role', { limit: 1000, order: [{ field: 'id', direction: 'asc' }] }, null, null, mode) as Observable<Array<RoleData>>;
    }

    getGroupsData(mode?:ResourceMode):Observable<Array<GroupData>> {
        return this.getResourceData('Group', { limit: 1000 }, null, null, mode) as Observable<Array<GroupData>>;
    }

    getConfigData():Observable<Array<ConfigData>> {
        return this.getResourceData('Configuration', { limit: 1000 }).pipe(map((data) => {
            for(let _item of data) {
                if(_item.tipo == 'resource') {
                    let _config = PGUtilities.tryParseJSON(_item.data);

                    if(_config.version == null) _config.version = 0;

                    if(_config.version < 1) {
                        if(typeof _config.views != 'undefined') {
                            _config.view = null;

                            if(_config.views != null && _config.views[0] != null) {
                                _config.view = _config.views[0];
                                delete _config.view.id;
                            }

                            delete _config.views;
                        }

                        if(typeof _config.filters != 'undefined') {
                            delete _config.filters;
                        }
                    }

                    if(_config.version != 1) {
                        _item.data = JSON.stringify(_config)
                        _config.version = 1;
                    }
                }
            }

            return data;
        })) as Observable<Array<ConfigData>>;
    }

    saveConfigData(element:string, sendData:ConfigData) {
        if(element == null || element == '') return this.postConfigData(sendData);
        else return this.putConfigData(element, sendData)
    }

    postConfigData(sendData?:ConfigData) {
        return this.postResourceData('Configuration', sendData);
    }

    putConfigData(element:string, sendData:ConfigData) {
        return this.putElementData('Configuration', element, sendData);
    }

    deleteConfigData(element:string) {
        return this.deleteElement('Configuration', element);
    }

    getPermissionData(isAdmin?:boolean):Observable<Array<PermissionData>> {
        if(isAdmin) {
            return this.getResourceData('Permission', { limit: 1000 }) as Observable<Array<PermissionData>>;
        }
        else {
            return new Observable<Array<PermissionData>>((observer) => {
                this.status.inProgress++;
    
                this.getRequest('/me/permission').subscribe(data => {
                    this.status.inProgress--;
    
                    if(data == null) data = [];
    
                    observer.next(data as Array<any>);
                    observer.unsubscribe();
                }, error => {
                    this.status.inProgress--;
    
                    console.debug('DATASERVICE - getPermissionData error:', error);
    
                    observer.next([]);
                    observer.unsubscribe();
                });
            })
        }
    }

    savePermissionData(element:string, sendData:PermissionData) {
        if(element == null || element == '') return this.postPermissionData(sendData);
        else return this.putPermissionData(element, sendData)
    }

    postPermissionData(sendData?:PermissionData) {
        return this.postResourceData('Permission', sendData);
    }
    
    putPermissionData(element:string, sendData:PermissionData) {
        return this.putElementData('Permission', element, sendData);
    }

    deletePermissionData(element:string) {
        return this.deleteElement('Permission', element);
    }

    private observableChainRec(observables:Array<Observable<any>>, observer:Subscriber<any>, index?:number) {
        if(index == null) index = 0;

        if(observables.length <= index) {
            observer.next();
            observer.unsubscribe();
        }
        else {
            observables[index].subscribe(() => {
                index++;
                this.observableChainRec(observables, observer, index);
            })
        }
    }

    observableChain(observables:Array<Observable<any>>) {
        return new Observable<any>((observer) => {
            this.observableChainRec(observables, observer)
        })
    }

    private _resourceFilters:{ 
        [resource:string]: {
            [filter:string]: Array<DataFilter>
        }
    } = {
        'Experience': {
            'Event': [{
                field: 'type', operator: '==', value: ['event']
            }],
            'Package': [{
                field: 'type', operator: '==', value: ['package']
            }],
            '': [{
                field: 'type', operator: '!=', value: ['event']
            }, {
                field: 'type', operator: '!=', value: ['package']
            }]
        }
    }

    getCleanResourceId(resource:string) {
        if(resource != null) {
            return resource.replace(/~.*$/, '');
        }
    }

    getResourceFilter(resource:string) {
        let _filterName = resource.split('~')[1];
        if(_filterName == null) _filterName = '';
        
        resource = this.getCleanResourceId(resource)

        if(this._resourceFilters[resource] != null && this._resourceFilters[resource][_filterName] != null) {
            return JSON.parse(JSON.stringify(this._resourceFilters[resource][_filterName])) as Array<DataFilter>;
        }
    }

    getResourceQueryURL(resource:string, options?:GetResourceDataOptions, relResource?: string, relElement?: string, withRealm?:boolean) {
        let _queryURL = '';

        let _resourceFilter = this.getResourceFilter(resource)
        resource = this.getCleanResourceId(resource)

        if(_resourceFilter != null) {
            if(options == null) options = {}
            else options = JSON.parse(JSON.stringify(options))
            
            if(!options.ignore_default_filters) {
                if(options.filter == null) options.filter = []
    
                for(let _item of _resourceFilter) {
                    options.filter.push(_item)
                }
            }
        }


        if(relResource != null && relElement != null) {
            _queryURL += encodeURIComponent(this.normalizeResourceName(relResource)) + '/' + relElement + '/';
        }

        _queryURL += encodeURIComponent(this.normalizeResourceName(resource));

        let _searchVal = this.getDataOptionsSearchString(resource, options)

        if(_searchVal != '') _queryURL += '?' + _searchVal;

        if(withRealm) {
            let _realmId = null;

            if(options.realm) {
                _realmId = options.realm;
            }
            else if(this.environmentService.ignoreRealm) _realmId = 'null';

            if(_realmId) {
                _queryURL += (_searchVal ? '&' : '?') + 'realm=' + _realmId
            }
        }

        return _queryURL;
    }

    private _getWithQuery(withList:Array<string>) {
        if(withList == null || withList.length == 0) return '';
        else {
            let _withQuery = '';
            for(let _cWith of withList) {
                if(_withQuery != '') _withQuery += ',';

                _withQuery += 'related_' + this.normalizeResourceName(_cWith);
            }

            return 'with=' + encodeURIComponent(_withQuery);
        }
    }

    private _normalizeTagsString(tags:string) {
        let _tagList = tags.split(/,|;/)

        for(let i = _tagList.length - 1; i >= 0; i--) {
            _tagList[i] = _tagList[i].replace(/^\s+/, '').replace(/\s+$/, '')
            if(_tagList[i] == '') _tagList.splice(i, 1)
        }

        return _tagList.join(',')
    }

    getDataOptionsSearchString(resource:string, options:GetResourceDataOptions) {
        resource = this.normalizeResourceName(resource);

        let _searchVal = '';

        if(options != null) {
            if(options.with != null && options.with.length > 0) {
                if(_searchVal != '') _searchVal += '&';
    
                _searchVal += this._getWithQuery(options.with)
            }
    
            if(options.search != null && options.search != '') {
                if(_searchVal != '') _searchVal += '&';
                _searchVal += 'search=' + encodeURIComponent(options.search);
            }

            if(options.tags != null && options.tags != '') {
                if(_searchVal != '') _searchVal += '&';
                _searchVal += 'tags=' + encodeURIComponent(this._normalizeTagsString(options.tags));
            }

            if(options.system_tags != null && options.system_tags != '') {
                if(_searchVal != '') _searchVal += '&';
                _searchVal += 'system_tags=' + encodeURIComponent(this._normalizeTagsString(options.system_tags));
            }
    
            if(options.filter != null && options.filter.length > 0) {
                if(_searchVal != '') _searchVal += '&';
    
                let _cFilter = JSON.parse(JSON.stringify(options.filter)) as DataFilter[];

                for(let _filter of _cFilter) {
                    if(_filter.resource != null && this.normalizeResourceName(_filter.resource) == resource) {
                        delete _filter.resource
                    }
                }
    
                _searchVal += 'filter=' + encodeURIComponent(JSON.stringify(_cFilter));
            }
    
            if(options.order != null && options.order.length > 0) {
                if(_searchVal != '') _searchVal += '&';
    
                let _cOrder = JSON.parse(JSON.stringify(options.order)) as DataOrder[];

                for(let _order of _cOrder) {
                    if(_order.resource != null && this.normalizeResourceName(_order.resource) == resource) {
                        delete _order.resource
                    }
                }
    
                _searchVal += 'order=' + encodeURIComponent(JSON.stringify(_cOrder));
            }
    
            if(options.offset != null) {
                if(_searchVal != '') _searchVal += '&';
                _searchVal += 'offset=' + encodeURIComponent(options.offset.toString());
            }
    
            if(options.limit != null) {
                if(_searchVal != '') _searchVal += '&';
                _searchVal += 'limit=' + encodeURIComponent(options.limit.toString());
            }
    
            if(options.view != null) {
                if(_searchVal != '') _searchVal += '&';
                _searchVal += 'view=' + encodeURIComponent(options.view);
            }
        }

        return _searchVal;
    }

    private _getPathFromResourceMode(mode:ResourceMode) {
        if(mode == 'admin') return 'superadmin'
        else return mode || 'data'
    }

    getResourceCounters(resource:string, options?:GetResourceDataOptions, relResource?: string, relElement?: string, mode?:ResourceMode, silent?:boolean):Observable<{ count: number }> {
        if(options == null) options = {};

        return new Observable<{ count: number }>((observer) => {
            let _queryURL = '/' + this._getPathFromResourceMode(mode) + '/counters/' + this.getResourceQueryURL(resource, options, relResource, relElement) // , true) NB: rimosso il force del realm per ora

            let _headers:HttpHeaders = null
            if(silent) {
                _headers = new HttpHeaders({ 'X-Interceptor-Silent': '*' })
            }              

            this.status.inProgress++;

            this.getRequest(_queryURL, { 
                headers: _headers
            }).subscribe(data => {

                this.status.inProgress--;

                observer.next(data);
                observer.unsubscribe();
            }, error => {
                this.status.inProgress--;

                console.debug('DATASERVICE - getResourceCounters error:', error);

                observer.next(null);
                observer.unsubscribe();
            });
        })
    }

    getResourceData(resource:string, options?:GetResourceDataOptions, relResource?: string, relElement?: string, mode?:ResourceMode, silent?:boolean):Observable<Array<any>> {
        if(options == null) options = {};

        return new Observable<Array<any>>((observer) => {
            let _queryURL = '/' + this._getPathFromResourceMode(mode) + '/' + this.getResourceQueryURL(resource, options, relResource, relElement, true)

            let _headers:HttpHeaders = null
            if(silent) {
                _headers = new HttpHeaders({ 'X-Interceptor-Silent': '*' })
            }              

            this.status.inProgress++;

            this.getRequest(_queryURL, { 
                headers: _headers
            }).subscribe(data => {

                this.status.inProgress--;

                if(data == null) data = [];

                observer.next(data as Array<any>);
                observer.unsubscribe();
            }, error => {
                this.status.inProgress--;

                console.debug('DATASERVICE - getResourceData error:', error);

                observer.next([]);
                observer.unsubscribe();
            });
        })
    }

    getElementData(resource:string, element:string, withRel?:Array<string>, mode?:ResourceMode, silent?:boolean):Observable<any> {

        if(this.isDraftId(element)) {
            return this.getDraftElementData(resource, element)
        }
        else {
            return new Observable<any>((observer) => {
                resource = this.getCleanResourceId(resource)

                let _queryURL = '/' + this._getPathFromResourceMode(mode) + '/' + this.normalizeResourceName(resource) + '/' + element;
            
                if(withRel != null && withRel.length > 0) {
                    _queryURL += '?' + this._getWithQuery(withRel);
                }

                let _headers:HttpHeaders = null
                if(silent) {
                    _headers = new HttpHeaders({ 'X-Interceptor-Silent': '*' })
                }
                
                this.status.inProgress++;

                this.getRequest(_queryURL, { 
                    headers: _headers
                }).subscribe(data => {

                    this.status.inProgress--;

                    observer.next(data);
                    observer.unsubscribe();
                }, error => {
                    this.status.inProgress--;

                    console.debug('DATASERVICE - getElementData error:', error);

                    observer.next(null);
                    observer.unsubscribe();
                });
            })
        }
    }

    getElementSnapshots(resource:string, element:string, options?:GetElementSnapshotsOptions, silent?:boolean):Observable<Array<any>> {
        resource = this.getCleanResourceId(resource)

        return new Observable<any>((observer) => {  
            resource = this.getCleanResourceId(resource)

            let _queryURL = '/snapshot/' + this.normalizeResourceName(resource) + '/' + element; 

            let _searchVal = ''

            if(options != null) {
                if(options.offset != null) {
                    if(_searchVal != '') _searchVal += '&';
                    _searchVal += 'offset=' + encodeURIComponent(options.offset.toString());
                }
        
                if(options.limit != null) {
                    if(_searchVal != '') _searchVal += '&';
                    _searchVal += 'limit=' + encodeURIComponent(options.limit.toString());
                }
            }

            if(_searchVal != '') {
                _queryURL += '?' + _searchVal
            }

            let _headers:HttpHeaders = null
            if(silent) {
                _headers = new HttpHeaders({ 'X-Interceptor-Silent': '*' })
            }
            
            this.status.inProgress++;

            this.getRequest(_queryURL, { 
                headers: _headers
            }).subscribe(data => {

                this.status.inProgress--;

                observer.next(data);
                observer.unsubscribe();
            }, error => {
                this.status.inProgress--;

                console.debug('DATASERVICE - getElementSnapshots error:', error);

                observer.next(null);
                observer.unsubscribe();
            });
        })
    }

    deleteElement(resource:string, element:string, mode?:ResourceMode, options?:any):Observable<boolean> {
        if(this.isDraftId(element)) {
            return this.deleteDraftElement(resource, element)
        }
        else {
            return new Observable<boolean>((observer) => {
                this.status.inProgress++;

                resource = this.getCleanResourceId(resource)

                this.deleteRequest('/' + this._getPathFromResourceMode(mode) + '/' + this.normalizeResourceName(resource) + '/' + element, options).subscribe(data => {

                    this.status.inProgress--;

                    observer.next(true);
                    observer.unsubscribe();
                }, error => {

                    this.status.inProgress--;

                    observer.next(false);
                    observer.unsubscribe();
                });
            })
        }
    }

    isSupplierResource(resource:string):boolean {
        let _resource = this.getCleanResourceId(resource);
        return _resource == 'Host' || _resource == 'Eatery' || _resource == 'ExperienceSupplier';
    }

    getSupplierAssociatedExperiences(resource:string, element:string, limit:number = 5):Observable<{ items: Array<any>, total: number }> {
        let _supplierKey = this.getCleanResourceId(resource) + '_' + element;
        let _filter:Array<DataFilter> = [{ field: 'supplier', operator: '==', value: [_supplierKey] }];
        let _baseOptions:GetResourceDataOptions = { filter: _filter, ignore_default_filters: true };

        return new Observable<{ items: Array<any>, total: number }>((observer) => {
            let _items:Array<any> = null;
            let _total:number = null;

            let _emitIfReady = () => {
                if(_items != null && _total != null) {
                    observer.next({ items: _items, total: _total });
                    observer.unsubscribe();
                }
            };

            this.getResourceData('Experience', { ..._baseOptions, limit: limit }).subscribe((data) => {
                _items = data || [];
                _emitIfReady();
            });

            this.getResourceCounters('Experience', _baseOptions).subscribe((data) => {
                _total = data != null && data.count != null ? data.count : 0;
                _emitIfReady();
            });
        });
    }

    putElementData(resource:string, element:string, sendData:any, mode?:ResourceMode, options?:any):Observable<any> {
        return new Observable<any>((observer) => {
            this.status.inProgress++;

            resource = this.getCleanResourceId(resource)

            if(typeof sendData.slug != 'undefined') delete sendData.slug;

            this.putRequest('/' + this._getPathFromResourceMode(mode) + '/' + this.normalizeResourceName(resource) + '/' + element, sendData, options).subscribe(data => {

                this.status.inProgress--;

                observer.next(data);
                observer.unsubscribe();
            }, error => {

                this.status.inProgress--;

                observer.next(null);
                observer.unsubscribe();
            });
        })
    }

    postResourceData(resource:string, sendData?:any, mode?:ResourceMode, options?:any):Observable<any> {
        if(sendData == null) sendData = {};
        
        if(typeof sendData.slug != 'undefined') delete sendData.slug;

        return new Observable<string>((observer) => {
            this.status.inProgress++;

            resource = this.getCleanResourceId(resource)

            this.postRequest('/' + this._getPathFromResourceMode(mode) + '/' + this.normalizeResourceName(resource), sendData, options).subscribe(data => {
                
                this.status.inProgress--;

                observer.next(data as any);
                observer.unsubscribe();
            }, error => {

                this.status.inProgress--;

                observer.next(null);
                observer.unsubscribe();
            });
        })
    }

    setElementSlug(resource:string, element:string, slug:string, mode?:ResourceMode) {
        return new Observable<string>((observer) => {
            this.status.inProgress++;

            resource = this.getCleanResourceId(resource)

            this.putRequest('/' + this._getPathFromResourceMode(mode) + '/' + this.normalizeResourceName(resource) + '/' + element, { slug: slug }, {
                headers: { 'X-Interceptor-Silent': '409' }
            }).subscribe(data => {
                this.status.inProgress--;

                observer.next(data.slug);
                observer.unsubscribe();
            }, (error) => {
                this.status.inProgress--;

                let _code = null;

                if(error.status == 409) {
                    _code = 'slug-taken'
                }

                observer.error(_code);
                observer.unsubscribe();
            });
        })
    }

    private _dateToQueryString(date:Date) {
        let _retVal = date.getFullYear() + '-';

        let _monthVal = (date.getMonth() + 1);
        _retVal += (_monthVal < 10 ? '0' : '') + _monthVal + '-'; 

        let _dayVal = date.getDate();
        _retVal += (_dayVal < 10 ? '0' : '') + _dayVal;

        return _retVal;
    }

    getElementAvailability(resource:string, element:string, from?:Date, toOrLimit?:Date|number):Observable<Array<AvailabilityData>> {

        resource = this.getCleanResourceId(resource)

        let _queryURL = '/data/' + this.normalizeResourceName(resource) + '/' + element + '/availability'

        let _searchVal = '';

        if(from != null) {
            _searchVal = 'from=' + this._dateToQueryString(from) + '-';
        }

        if(toOrLimit != null)  {
            if(_searchVal != '') _searchVal += '&';

            if(typeof toOrLimit == 'number') {
                _searchVal += 'limit=' + toOrLimit;
            }
            else {
                _searchVal += 'to=' + this._dateToQueryString(toOrLimit);
            }
        }

        if(_searchVal != '') _queryURL += '?' + _searchVal;

        return new Observable<Array<AvailabilityData>>((observer) => {
            this.status.inProgress++;

            this.getRequest(_queryURL).subscribe((data:any) => {

                this.status.inProgress--;

                // Ottieni offset timezone (es: +02:00)
                let _timeZone = DateTime.now().setZone(data.timezone).toFormat('ZZ');

                let _retData:Array<AvailabilityData> = [];

                if(data != null && data.slots != null) {                    

                    for(let _cData of data.slots) {
                        let _cStart = _cData.start_day + 'T' + _cData.start_time + 'Z' + _timeZone;
                        let _cEnd = _cData.end_day + 'T' + _cData.end_time + 'Z' + _timeZone;

                        // verifica correttezza, alcuni giorni mi arrivano con durata 0
                        // TODO: andrebbe fatto lato server, mi dovrebbe tornare un certo numero di risultati buoni

                        let _cStartDate = new Date(_cStart);
                        let _cEndDate = new Date(_cEnd);

                        if(_cEndDate > _cStartDate) {
                            _retData.push({
                                start: _cStartDate.toISOString(),
                                end: _cEndDate.toISOString(),
                                status: _cData.status
                            })
                        }
                    }
                }

                observer.next(_retData);
                observer.unsubscribe();
            }, error => {
                this.status.inProgress--;

                console.debug('DATASERVICE - getElementAvailability error:', error);

                observer.next(null);
                observer.unsubscribe();
            });
        })
    }

    private async _getPostFileInfo(fileObj:File) {
        return new Promise<any>((resolve) => {
            if(/^image/.test(fileObj.type)) {
                let _fr = new FileReader();

                _fr.onload = () => {
                    let _img = new Image()
                    
                    _img.onload = () => {
                        resolve({
                            width: _img.naturalWidth,
                            height: _img.naturalHeight,
                        })
                    }

                    _img.onerror = () => {
                        resolve(null)
                    }

                    _img.src = _fr.result as string
                }

                _fr.onerror = () => {
                    resolve(null)
                }

                _fr.readAsDataURL(fileObj);
            }
            else {
                resolve(null)
            }
        })
    }

    postFile(resource:string, fileObj:File|string, data?:PgPostFileData, silent?:boolean): Observable<DataProgress> {
        return new Observable<DataProgress>((observer) => {
            this.status.inProgress++;

            let formData = new FormData();

            if(typeof fileObj == 'string') {
                formData.append('external_url', fileObj);
            }
            else {
                let _fileName = fileObj.name.replace(/\.[^\.]*$/, (val) => {
                    return val.toLowerCase();
                })

                formData.append('fileToUpload', fileObj, _fileName);
            }


            if(data != null) {
                for(let i in data) {
                    if(data[i] != null) {
                        formData.append(i, data[i]);
                    }
                }
            }

            let _headers:HttpHeaders = null
            if(silent) {
                _headers = new HttpHeaders({ 'X-Interceptor-Silent': '*' })
            }

            resource = this.getCleanResourceId(resource)

            let req = new HttpRequest('POST', this.environmentService.environment.APIUrl + '/data/' + this.normalizeResourceName(resource), 
                formData, 
                { 
                    reportProgress: true,
                    headers: _headers
                });
        
            this.http.request(req).subscribe((event: HttpEvent<any>) => {

                switch (event.type) {
                    case HttpEventType.UploadProgress:
                        
                        observer.next(new DataProgress(Math.min(0.99, event.loaded / event.total)));

                        break;
                    case HttpEventType.ResponseHeader:

                        if(!event.ok) { // ERROR
                            this.status.inProgress--;

                            observer.next(new DataProgress(1, true, null, event));

                            observer.unsubscribe();
                        }

                        break;
                    case HttpEventType.Response:

                        this.status.inProgress--;

                        observer.next(new DataProgress(1, true, event.body));
                        observer.unsubscribe();

                        break;
                }
            })
        })
    }

    viewTour(id:string) {
        return new Observable<WebTour>((observer) => {
            this.getElementData('TourProduct', id).subscribe((data) => {
                let _retVal = new WebTour(data);

                observer.next(_retVal);
                observer.unsubscribe();
            })
        })
    }

    viewTours() {
        return new Observable<Array<WebTour>>((observer) => {
            this.getResourceData('TourProduct').subscribe((data) => {
                let _retVal:Array<WebTour> = [];

                for(let _cData of data) {
                    let _cTour = new WebTour(_cData);

                    _retVal.push(_cTour);
                }

                observer.next(_retVal);
                observer.unsubscribe();
            }, () => {
                observer.next(null);
                observer.unsubscribe();
            })
        })
    }

    getTour(id:string) {
        return new Observable<WebTour>((observer) => {
            this.getElementData('TourProduct', id).subscribe((data) => {
                let _retVal = new WebTour(data);

                observer.next(_retVal);
                observer.unsubscribe();
            })
        })
    }

    getTours() {
        return new Observable<Array<WebTour>>((observer) => {
            this.getResourceData('TourProduct').subscribe((data) => {
                let _retVal:Array<WebTour> = [];

                for(let _cData of data) {
                    let _cWebTour = new WebTour(_cData);

                    _retVal.push(_cWebTour);
                }

                observer.next(_retVal);
                observer.unsubscribe();
            }, () => {
                observer.next(null);
                observer.unsubscribe();
            })
        })
    }

    saveTour(tour:WebTour) {
        return new Observable<WebTour>((observer) => {
            this.putElementData('TourProduct', tour.id, tour).subscribe((data) => {
                if(data === null) {
                    observer.next(null);
                    observer.unsubscribe();
                }
                else {
                    let _cData = data;

                    this.saveElementTranslations(tour.id, 'TourProductTranslation', 'tour_product_id', data.languages, tour.translations).subscribe((data) => {
                        _cData.translations = data;

                        observer.next(new WebTour(data));
                        observer.unsubscribe();
                    }, () => {
                        observer.next(null);
                        observer.unsubscribe();
                })
                }
            }, () => {
                observer.next(null);
                observer.unsubscribe();
            })
        })
    }

    createTour() {
        return this.postResourceData('TourProduct', {
            name: 'Nuovo tour',
            description: 'Nuovo tour'
        }) as Observable<WebTour>;
    }

    deleteTour(id:string) {
        return this.deleteElement('TourProduct', id);
    }

    broadcastMessage(list:'realm', content:string, subject?:string) {
        return new Observable<boolean>((observer) => {
            this.postRequest('/messages/broadcast/' + encodeURIComponent(list), {
                subject: subject, 
                content: content
            }).subscribe((data) => {
                observer.next(true);
                observer.unsubscribe();
            }, (error) => {
                observer.error(error);
                observer.unsubscribe();
            })
        })
    }

    broadcastAdminMessage(list:'admins', content:string, subject?:string) {
        return new Observable<boolean>((observer) => {
            this.postRequest('/' + this._getPathFromResourceMode('admin') + '/messages/broadcast/' + encodeURIComponent(list), {
                subject: subject, 
                content: content
            }).subscribe((data) => {
                observer.next(true);
                observer.unsubscribe();
            }, (error) => {
                observer.error(error);
                observer.unsubscribe();
            })
        })
    }

    sendAdminMessage(via:'phone'|'email', recipient:string, content:string, subject?:string) {
        return new Observable<boolean>((observer) => {
            recipient = PGUtilities.cleanRecipient(recipient, via);

            this.postRequest('/' + this._getPathFromResourceMode('admin') + '/messages/' + via + '/' + encodeURIComponent(recipient), {
                subject: subject, 
                content: content
            }).subscribe((data) => {
                observer.next(true);
                observer.unsubscribe();
            }, (error) => {
                observer.error(error);
                observer.unsubscribe();
            })
        })
    }

    sendCustomMessage(via:'phone'|'email'|'list', recipient:string, content:string, subject?:string) {
        return new Observable<boolean>((observer) => {
            recipient = PGUtilities.cleanRecipient(recipient, via);

            this.postRequest('/messages/single/' + via + '/' + encodeURIComponent(recipient), {
                'subject': subject, 
                'content': content
            }).subscribe((data) => {
                observer.next(true);
                observer.unsubscribe();
            }, (error) => {
                observer.error(error);
                observer.unsubscribe();
            })
        })
    }

    sendWebTourMessage(tourId:string, via:'phone'|'email'|'link', recipient:string) {
        return new Observable<{ link: string }>((observer) => {
            if(via == 'link') {
                this.postRequest('/messages/tour/' + tourId, null).subscribe((data) => {
                    observer.next(data);
                    observer.unsubscribe();
                }, (error) => {
                    observer.error(error);
                    observer.unsubscribe();
                })
            }
            else {
                recipient = PGUtilities.cleanRecipient(recipient, via);

                this.postRequest('/messages/tour/' + tourId + '/' + via + '/' + encodeURIComponent(recipient), null).subscribe((data) => {
                    observer.next(data);
                    observer.unsubscribe();
                }, (error) => {
                    observer.error(error);
                    observer.unsubscribe();
                })
            }
        })
    }

    sendWebTourDirectorMessage(tourId:string, via:'phone'|'email'|'list', recipient:string) {
        return new Observable<{ token:string }>((observer) => {
            recipient = PGUtilities.cleanRecipient(recipient, via);

            this.postRequest('/messages/tourDirector/' + via + '/' + encodeURIComponent(recipient) + '/' + tourId, null).subscribe((data) => {
                observer.next(data as { token:string });
                observer.unsubscribe();
            }, (error) => {
                observer.error(error);
                observer.unsubscribe();
            })
        })
    }

    shortURL(url:string) {
        return new Observable<{ url: string, short: string }>((observer) => {
            this.postRequest('/utility/short', { 
                'url': url
            }).subscribe((data) => {
                observer.next(data as { url: string, short: string });
                observer.unsubscribe();
            }, (error) => {
                observer.error(error);
                observer.unsubscribe();
            })
        })
    }

    saveElementData(resource:string, element:string, sendData:any, mode?:ResourceMode):Observable<any> {
        if(element == null || element == '') {
            return this.postResourceData(resource, sendData, mode)
        }
        else if(this.isDraftId(element)) {
            return this.postDraftElementData(resource, element, sendData)
        }
        else {
            return this.putElementData(resource, element, sendData, mode)
        }
    }

    getElementTranslations(resourceId:string, elementId:string, translationResource:string) {
        return new Observable<{ [language:string]: any }>((observer) => {
            this.getResourceData(translationResource, null, resourceId, elementId).subscribe((translData) => {   
                let _retVal:{ [language:string]: any } = {} 

                for(let _cTransl of translData) {
                    _retVal[_cTransl.language] = _cTransl;
                }
    
                observer.next(_retVal);
                observer.unsubscribe();
            })
        })
    }

    saveElementTranslations(elementId:string, resourceId:string, joinField:string, languages:Array<string>, translations: { [language:string]: any }) {
        return new Observable<{ [language:string]: any }> ((observer) => {

            let _translationList:Array<any> = [];

            let _languageList = this.environmentService.getRealmLanguages(this.authService.user?.realm?.id)

            for(let _language of _languageList) {
                if(translations[_language] == null) {
                    translations[_language] = {
                        auto: true,
                        language: _language
                    }
                }
                else {
                    if(languages != null) {
                        translations[_language].auto = languages.indexOf(_language) == -1;
                    }
                    else {
                        translations[_language].auto = _language != _languageList[0] // sembra che se non c'è un campo lingue viene tradotto solo nella prima lingua
                    }
                }

                _translationList.push(translations[_language])
            }

            _translationList.sort((a, b) => {
                if(!a.auto && b.auto) return -1;
                else if(a.auto && !b.auto) return 1;
                else return 0;
            })

            let _reqCount = 0;

            let _retVal:{ [language:string]: any } = {};

            for(let _translation of _translationList) {
                if(_translation.id != null) {
                    _reqCount++;
                    this.putElementData(resourceId, _translation.id, _translation).subscribe((data) => {
                        for(let j in data) {
                            _translation[j] = data[j];
                        }

                        _reqCount--;
                        if(_reqCount == 0) {
                            observer.next(_retVal);
                            observer.unsubscribe();
                        }
                    })
                }
                else {
                    let _hasSomethingToSave = false;

                    for(let j in _translation) {
                        if(j != 'language' && j != joinField && _translation[j] != null) {
                            _hasSomethingToSave = true;
                            break;
                        }
                    }

                    if(_hasSomethingToSave) {
                        _reqCount++;
                        _translation[joinField] = elementId;

                        this.postResourceData(resourceId, _translation).subscribe((data) => {
                            for(let j in data) {
                                _translation[j] = data[j];
                            }

                            _reqCount--;
                            if(_reqCount == 0) {
                                observer.next(_retVal);
                                observer.unsubscribe();
                            }
                        })
                    }
                }
            }

            if(_reqCount == 0) {
                observer.next(_retVal);
                observer.unsubscribe();
            }
        })
    }

    isDraftId(id:string|number) {
        return typeof id == 'string' && id.startsWith('draft_')
    }

    generateDraftId() {
       return 'draft_' + PGUtilities.getRandomId();
    }

    getResourceDrafts(resourceId:string) {
        return new Observable<{ [id:string]: any }>((observer) => {
            setImmediate(() => {
                let _cResourceDraftObject = {}
                let _cResourceDraftString = this.persistenceService.getItem('PGDraft_' + resourceId);

                if(_cResourceDraftString != null) {
                    _cResourceDraftObject = JSON.parse(_cResourceDraftString);
                }

                observer.next(_cResourceDraftObject);
                observer.unsubscribe();
            })
        })
    }

    createDraftElement(resourceId:string, draftData?:any) {
        // teniamo un draft per risorsa per ora
        this.deleteResourceDrafts(resourceId);

        if(draftData == null) draftData = {};

        draftData.id = this.generateDraftId()

        return this.saveDraftElementData(resourceId, draftData.id, draftData);
    }

    getDraftElementData(resourceId:string, element:string) {
        return new Observable<any>((observer) => {
            setImmediate(() => {
                let _retVal = null;

                let _cResourceDraftString = this.persistenceService.getItem('PGDraft_' + resourceId);

                if(_cResourceDraftString != null) {
                    let _cResourceDraftObject = JSON.parse(_cResourceDraftString);
                    if(_cResourceDraftObject[element] != null) {
                        _retVal = _cResourceDraftObject[element];
                    }
                }

                observer.next(_retVal);
                observer.unsubscribe();
            })
        })
    }

    saveDraftElementData(resourceId:string, element:string, draftData:any) {
        return new Observable<any>((observer) => {
            setImmediate(() => {
                let _cResourceDraftObject = {}
                let _cResourceDraftString = this.persistenceService.getItem('PGDraft_' + resourceId);

                if(_cResourceDraftString != null) {
                    _cResourceDraftObject = JSON.parse(_cResourceDraftString);
                }

                _cResourceDraftObject[element] = draftData;

                this.persistenceService.setItem('PGDraft_' + resourceId, JSON.stringify(_cResourceDraftObject), 'technical');

                observer.next(draftData);
                observer.unsubscribe();
            })
        })
    }

    deleteResourceDrafts(resourceId:string) {
        this.persistenceService.removeItem('PGDraft_' + resourceId);
    }

    deleteDraftElement(resourceId:string, element:string) {
        return new Observable<boolean>((observer) => {
            setImmediate(() => {
                let _cResourceDraftString = this.persistenceService.getItem('PGDraft_' + resourceId);

                if(_cResourceDraftString != null) {
                    let _cResourceDraftObject = JSON.parse(_cResourceDraftString);
                    
                    if(_cResourceDraftObject[element] != null) {
                        delete _cResourceDraftObject[element];

                        this.persistenceService.setItem('PGDraft_' + resourceId, JSON.stringify(_cResourceDraftObject), 'technical');
                    }
                }

                observer.next(true);
                observer.unsubscribe();
            })
        })
    }

    postDraftElementData(resourceId:string, element:string, sendData:any) {
        return new Observable<any>((observer) => {
            let _cSendData = JSON.parse(JSON.stringify(sendData));
            _cSendData.id = null;

            this.postResourceData(resourceId, _cSendData).subscribe((data) => {
                this.deleteDraftElement(resourceId, element).subscribe(() => {    
                    observer.next(data);
                    observer.unsubscribe();
                }, () => {    
                    observer.next(data);
                    observer.unsubscribe();
                });
            }, () => {
                observer.error();
                observer.unsubscribe();
            })
        })
    }

    getEnvironmentData() {
        return new Observable<Array<any>>((observer) => {
            this.status.inProgress++;

            this.getRequest('/superadmin/environment?limit=1000').subscribe(data => {

                this.status.inProgress--;

                if(data == null) data = [];

                observer.next(data as Array<any>);
                observer.unsubscribe();
            }, error => {
                this.status.inProgress--;

                console.debug('DATASERVICE - getEnvironmentData error:', error);

                observer.next([]);
                observer.unsubscribe();
            });
        })
    }

    postEnvironmentData(sendData:any):Observable<any> {
        if(sendData == null) sendData = {};

        return new Observable<string>((observer) => {
            this.status.inProgress++;

            this.postRequest('/superadmin/environment', sendData).subscribe(data => {
                
                this.status.inProgress--;

                observer.next(data as any);
                observer.unsubscribe();
            }, error => {

                this.status.inProgress--;

                observer.next(null);
                observer.unsubscribe();
            });
        })
    }

    saveEnvironmentData(element:string, sendData:any):Observable<any> {
        return new Observable<any>((observer) => {
            this.status.inProgress++;

            this.putRequest('/superadmin/environment/' + element, sendData).subscribe(data => {

                this.status.inProgress--;

                observer.next(data);
                observer.unsubscribe();
            }, error => {

                this.status.inProgress--;

                observer.next(null);
                observer.unsubscribe();
            });
        })
    }

    getNotifications(options?:GetResourceDataOptions, relResource?: string, relElement?: string, silent?:boolean):Observable<Array<any>> {
        if(options == null) options = {};

        let _queryURL = '/' + this.getResourceQueryURL('Notification', options, relResource, relElement)

        return new Observable<Array<any>>((observer) => {
            this.status.inProgress++;

            let _reqOptions = null;

            if(silent) {
                _reqOptions = {
                    headers: { 'X-Interceptor-Silent': '*' }
                }
            }

            this.getRequest(_queryURL, _reqOptions).subscribe(data => {

                this.status.inProgress--;

                if(data == null) data = [];

                observer.next(data as Array<any>);
                observer.unsubscribe();
            }, error => {
                this.status.inProgress--;

                console.debug('DATASERVICE - getNotifications error:', error);

                observer.next([]);
                observer.unsubscribe();
            });
        })
    }

    private _mapConnectorResource = {
        'DHM': {
            'Poi': 'destination',
            'Itinerary': 'itinerary',
            'Experience': 'experience',
            'Experience~Package': 'experience',
            'Experience~Event': 'event',
            'Host': 'host',
            'Eatery': 'eatery',
            'ExperienceSupplier': 'supplier',
            'Article': 'article',
        },
        'TDH': {
            'Poi': 'destination',
            'Itinerary': 'itinerary',
            'Experience': 'experience',
            'Experience~Package': 'experience',
            'Experience~Event': 'event',
            'Article': 'article',
        }
    }

    getConnectorResources(connector:ConnectorType) {
        let _result:Array<string> = []

        if(this._mapConnectorResource[connector] != null) {
            for(let i in this._mapConnectorResource[connector]) {
                let _resource = i.replace(/~.*$/, '')

                if(_result.indexOf(_resource) == -1) {
                    _result.push(_resource)
                }
            }
        }

        return _result;
    }

    getDMSResourceByConnectorResource(connector:ConnectorType, resource:string) {
        if(this._mapConnectorResource[connector] != null) {
            for(let i in this._mapConnectorResource[connector]) {
                if(this._mapConnectorResource[connector][i] == resource) {
                    return i;
                }
            }
        }
    }

    canPublishOn(connector:ConnectorType, resource:string, realm:string) {
        return this.environmentService.isConnectorActive(connector, realm) && this._mapConnectorResource[connector] != null && this._mapConnectorResource[connector][resource] != null;
    }

    private async _checkBeforePublish(resource:string, element:string) {
        if(resource != 'Itinerary') {
            return true
        }
        else {
            let _data = await new Promise<any>((resolve) => {
                this.getElementData(resource, element).subscribe((data) => {
                    resolve(data)
                })
            })

            if(_data == null) return false;
            else {
                let _didChange = false;
    
                if(_data.collection != null) {
                    for(let _item of _data.collection) {
                        if(_item.id == null) {
                            _item.id = PGUtilities.getRandomId()
                            _didChange = true;
                        }
                    }
                }

                if(_data.services != null) {
                    for(let _item of _data.services) {
                        if(_item.id == null) {
                            _item.id = PGUtilities.getRandomId()
                            _didChange = true;
                        }
                    }
                }
    
                if(!_didChange) {
                    return true;
                }
                else {
                    return await new Promise<boolean>((resolve, reject) => {
                        this.putElementData(resource, element, _data).subscribe((data) => {
                            resolve(data != null)
                        })
                    })
                }
            }
        }
    }

    doPublishOn(connector:ConnectorType, resource:string, element:string, realm:string) {
        return new Observable<any>((observer) => {
            if(this.canPublishOn(connector, resource, realm)) {
                this._checkBeforePublish(resource, element).then((result) => {
                    if(!result) {
                        observer.next();
                        observer.unsubscribe();
                    }
                    else {
                        this.status.inProgress++;
                        let _connectorResource = this._mapConnectorResource[connector][resource];
            
                        this.postRequest('/connectors/' + connector.toLowerCase() + '/' + _connectorResource + '/' + element, null).subscribe(() => {
                            this.getElementData(resource, element).subscribe((data) => {
                                this.status.inProgress--;
            
                                observer.next(data);
                                observer.unsubscribe();
                            }, () => {
                                this.status.inProgress--;
            
                                observer.next();
                                observer.unsubscribe();
                            })
                        }, error => {
                            this.status.inProgress--;
            
                            console.debug('DATASERVICE - doPublishOn error:', error);
            
                            observer.next();
                            observer.unsubscribe();
                        })
                    }
                })
            }
            else {
                setImmediate(() => {
                    observer.next();
                    observer.unsubscribe();
                })
            }
        })
    }

    getSearchTags(search?:string) {
        return new Observable<Array<any>>((observer) => {
            this.status.inProgress++;

            let _searchQuery = ''
            if(search) _searchQuery = '?q=' + encodeURIComponent(search)

            this.getRequest('/data/search/tag' + _searchQuery).subscribe((data) => {
                this.status.inProgress--;

                observer.next(data);
                observer.unsubscribe();
            }, error => {
                this.status.inProgress--;

                console.debug('DATASERVICE - getTags error:', error);

                observer.next([]);
                observer.unsubscribe();
            })
        })
    }

    getConnectorExports(connector:string, from:Date, to:Date, realm?:string) {
        return new Observable<Array<any>>((observer) => {
            connector = connector.toLowerCase()

            let _url:string = '/connectors/report?connector=' + connector + '&start_date=' + this._dateToQueryString(from) + '&end_date=' + this._dateToQueryString(to)

            if(realm != null) {
                _url += '&realm_id=' + realm;
            }


            this.status.inProgress++;

            this.getRequest(_url).subscribe((data) => {
                this.status.inProgress--;

                observer.next(data);
                observer.unsubscribe();
            }, error => {
                this.status.inProgress--;

                console.debug('DATASERVICE - getConnectorExports error:', error);

                observer.next([]);
                observer.unsubscribe();
            })
        })
    }

    getConnectorExportsV2(connector:string, fromDate:Date, toDate:Date, productType?:string, status?:string, realmId?:string, page?:number, perPage?:number) {
        return new Observable<{ data: Array<ConnectorExportData>, meta: { total: number } }>((observer) => {
            connector = connector.toLowerCase()

            let _url = '/connectors/' + connector + '/report?start_date=' + this._dateToQueryString(fromDate) + '&end_date=' + this._dateToQueryString(toDate)

            if(productType != null) {
                _url += '&product_type=' + productType;
            }

            if(status != null) {
                _url += '&status=' + status;
            }

            if(typeof realmId != 'undefined') {
                _url += '&realm_id=' + (realmId ? realmId : 'null');
            }

            if(perPage != null) {
                _url += '&per_page=' + perPage;
            }

            if(page != null) {
                _url += '&page=' + (page + 1);
            }

            this.status.inProgress++;

            this.getRequest(_url).subscribe((data) => {

                // TODO: da rimuovere quando il BE sarà allineato
                data.meta = data.meta || data.pagination;

                this.status.inProgress--;

                observer.next(data);
                observer.unsubscribe();
            }, error => {
                this.status.inProgress--;

                console.debug('DATASERVICE - getConnectorExportsV2 error:', error);

                observer.next(null);
                observer.unsubscribe();
            })
        })
    }

    private _resolveDirectoryPath(observer:Subscriber<string>, path:Array<string>, create?:boolean, mode?:ResourceMode, parent?:string) {
        if(path.length == 0) {
            observer.next(parent)
            observer.unsubscribe()
        }
        else {
            let _directory = path.shift()
    
            this.getResourceData('Directory', {
                filter: [{
                    field: 'parent_id', operator: '==', value: [parent]
                }, {
                    field: 'label', operator: '==', value: [_directory]
                }]
            }, null, null, mode).subscribe((data) => {
                if(data != null && data[0] != null) {
                    this._resolveDirectoryPath(observer, path, create, mode, data[0].id)
                }
                else {
                    if(!create) {
                        observer.next(null)
                        observer.unsubscribe()
                    }
                    else {
                        this.postResourceData('Directory', { parent_id: parent, name: _directory }, mode).subscribe((data) => {
                            this._resolveDirectoryPath(observer, path, create, mode, data.id)
                        })
                    }
                }
            })
        }
    }

    resolveDirectoryPath(path:Array<string>, create?:boolean, mode?:ResourceMode) {
        return new Observable<string>((observer) => {
            this._resolveDirectoryPath(observer, path, create, mode, null)
        })
    }

    sendInviteEmail(realm:string, email:string, language:string, role:'business'|'editor') {
        return new Observable<boolean>((observer) => {
            this.http.post(this.environmentService.environment.APIUrl + '/realm/' + realm + '/invite', {
                contact: email,
                language: language,
                role: role
            }, {
                headers:  { 'X-Interceptor-Silent': '404' }
            }).subscribe(() => {
                observer.next(true)
                observer.unsubscribe()
            }, () => {
                observer.next(false)
                observer.unsubscribe()
            })
        })
    }

    getPendingInvites(realm:string) {
        return new Observable<any>((observer) => {
            this.http.get(this.environmentService.environment.APIUrl + '/realm/' + realm + '/invitations').subscribe((data) => {
                observer.next(data)
                observer.unsubscribe()
            }, () => {
                observer.next(false)
                observer.unsubscribe()
            })
        })
    }
}